import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import TomaPedidos from '../../TomaPedidos'; // El nuevo componente header
import CreacionPedido from '../../CreacionPedido/CreacionPedido';
import MeseroGridSelector from '../../shared/selectors/SeleccionMeseroHorizontal';
import CambioMesaModal from '../../shared/modals/CambioMesaModal';
import API from '../../../../utils/api';
import './PedidosMesa.css';

// Custom hooks
import usePedido from '../../../../hooks/usePedido';
import usePromociones from '../../../../hooks/usePromociones';
import ComandaService from '../../../../services/ComandaService';
import { useToast } from '../../../../contexts/ToastContext';

// Servicios de impresión
import VerificadorImpresorasService from '../../../../services/VerificadorImpresorasService';
import DialogoAlertaImpresoras from '../../../Impresion/DialogoAlertaImpresoras';
import BotonSemaforoImpresoras from '../../../Impresion/BotonSemaforoImpresoras';
import ImpresionMonitor from '../../../Impresion/ImpresionMonitor';

const verificadorImpresoras = VerificadorImpresorasService;

// Utilidad para generar IDs únicos
const generarIdUnico = () => `${Date.now()}-${Math.floor(Math.random() * 10000)}`;

/**
 * Componente específico para manejar pedidos a mesa
 * Utiliza TomaPedidos como header y maneja toda la lógica de productos/categorías
 */
const PedidosMesa = () => {
  const montadoRef = useRef(true);
  const { idMesa } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const inicializacionCompleta = useRef(false);

  // ===== ESTADOS PRINCIPALES =====
  const [loading, setLoading] = useState(true);
  const [loadingProductos, setLoadingProductos] = useState(false);
  const [error, setError] = useState(null);

  // Estados de la mesa
  const [datosMesa, setDatosMesa] = useState(null);
  const [mesero, setMesero] = useState(null);
  const [numeroPersonas, setNumeroPersonas] = useState(1);
  const [numeroCuenta, setNumeroCuenta] = useState('00');

  // Estados de productos y categorías
  const [categorias, setCategorias] = useState([]);
  const [productos, setProductos] = useState([]);
  const [categoriaSeleccionada, setCategoriaSeleccionada] = useState(null);
  const [promocionesDisponibles, setPromocionesDisponibles] = useState([]);

  // Estados del pedido
  const [modoEdicion, setModoEdicion] = useState(false);
  const [pedidoExistente, setPedidoExistente] = useState(null);
  const [fechaHora, setFechaHora] = useState(new Date());

  // Estados de modales
  const [modalMeseroVisible, setModalMeseroVisible] = useState(false);
  const [modalCambioMesaVisible, setModalCambioMesaVisible] = useState(false);
  const [modalNumeroPersonas, setModalNumeroPersonas] = useState(false);
  const [mostrarDialogoImpresoras, setMostrarDialogoImpresoras] = useState(false);
  const [mostrarMonitorImpresoras, setMostrarMonitorImpresoras] = useState(false);
  const [resultadoVerificacionImpresoras, setResultadoVerificacionImpresoras] = useState(null);
  const [pedidoId, setPedidoId] = useState(null);

  // Estados temporales para edición
  const [numeroPersonasTemp, setNumeroPersonasTemp] = useState(numeroPersonas);

  // ===== HOOKS PERSONALIZADOS =====

  // Hook de pedido - USANDO LAS FUNCIONES CORRECTAS DE TU CÓDIGO ORIGINAL
  const {
    pedidoActual,
    productosNuevos,
    productosGuardados,
    agregarProducto,
    cambiarCantidad,
    eliminarProducto,
    eliminarProductoGuardado,
    limpiarPedido,
    inicializarPedido,
    imprimirComanda, // ← FUNCIÓN REAL DEL HOOK
    agregarObservaciones,
    calcularTotales,
    buscarProducto,
    buscarProductoSimilar,
    recalcularPromociones: recalcularPromocionesHook
  } = usePedido();

  // Hook de promociones
  const {
    promocionesAplicadas,
    totalDescuentos,
    calculandoPromociones,
    calcularTotales: calcularTotalesPromociones,
    recalcularPromociones
  } = usePromociones(pedidoActual, promocionesDisponibles);

  // ===== CONFIGURACIÓN DEL SISTEMA =====
  const systemConfig = useMemo(() => {
    try {
      return API.auth?.getSystemConfig() || {};
    } catch (error) {
      console.warn('Error al obtener configuración del sistema:', error);
      return { impresoraPorDefecto: 'Local' };
    }
  }, []);

  // ===== EFECTOS =====

  // Efecto para actualizar fecha y hora
  useEffect(() => {
    const interval = setInterval(() => {
      setFechaHora(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Cargar datos iniciales al montar
  useEffect(() => {
    cargarDatosIniciales();
  }, []);

  // Efecto para recalcular promociones
  useEffect(() => {
    if (recalcularPromociones && pedidoActual.length > 0) {
      recalcularPromociones();
    }
  }, [pedidoActual, recalcularPromociones]);

  // Efecto para sincronizar número de personas temporal
  useEffect(() => {
    setNumeroPersonasTemp(numeroPersonas);
  }, [numeroPersonas]);

  // ===== FUNCIONES DE CARGA DE DATOS (TU CÓDIGO ORIGINAL) =====

  // Cargar categorías y productos
  const cargarCategoriaYProductos = useCallback(async () => {
    try {
      setLoadingProductos(true);

      // Cargar categorías
      const categoriasData = await API.categorias.getAll();
      setCategorias(categoriasData);

      // Si hay categorías y no hay una seleccionada, seleccionar la primera
      if (categoriasData && categoriasData.length > 0 && !categoriaSeleccionada) {
        setCategoriaSeleccionada(categoriasData[0]);

        // Cargar productos de la primera categoría
        const productosData = await API.productos.getPorCategoria(categoriasData[0].idCategoria);
        setProductos(productosData);
      }

      setLoadingProductos(false);
    } catch (err) {
      console.error("Error al cargar categorías o productos:", err);
      setLoadingProductos(false);
      toast.error("Error al cargar categorías o productos");
    }
  }, [categoriaSeleccionada, toast]);

  // Cargar datos iniciales
  const cargarDatosIniciales = useCallback(async () => {
    if (inicializacionCompleta.current) return;

    try {
      setLoading(true);

      const [mesaData, respuestaPedido, promocionesData] = await Promise.all([
        API.mesas.getById(idMesa),
        API.pedidos.verificarPedidoEnMesa(idMesa),
        API.promociones.getActivas()
      ]);

      console.log("🔍 DEBUG verificarPedidoEnMesa response:", respuestaPedido);

      // Solo establecer pedidoId si REALMENTE existe un pedido
      if (respuestaPedido.existePedido && respuestaPedido.pedido?.idPedido) {
        console.log("🔍 Pedido existente encontrado, ID:", respuestaPedido.pedido.idPedido);
        setPedidoId(respuestaPedido.pedido.idPedido);
      } else {
        console.log("🆕 No hay pedido existente en esta mesa");
        setPedidoId(null);
      }

      setDatosMesa(mesaData);

      if (promocionesData) {
        setPromocionesDisponibles(promocionesData);
      }

      await cargarCategoriaYProductos();

      if (respuestaPedido.existePedido) {
        const pedidoData = await API.pedidos.getPedido(respuestaPedido.pedido.idPedido);

        setNumeroPersonas(pedidoData.numeroPersonas || 1);
        setNumeroCuenta(pedidoData.numeroDia || '00');

        if (pedidoData.mesero) {
          setMesero(pedidoData.mesero);
        }

        if (pedidoData.detalles && pedidoData.detalles.length > 0) {
          console.log(`📋 CARGANDO PEDIDO EXISTENTE - ID: ${respuestaPedido.pedido.idPedido}`);
          console.log(`📦 Detalles encontrados: ${pedidoData.detalles.length} productos`);

          // Función para detectar si un producto está guardado/impreso
          const esProductoGuardado = (detalle) => {
            return detalle.status === 'guardado' ||
              detalle.impreso === true ||
              detalle.impreso === 'true' ||
              detalle.impreso === 1 ||
              detalle.impreso === '1';
          };

          const itemsPedido = pedidoData.detalles.map((detalle, index) => {
            const esGuardado = esProductoGuardado(detalle);

            console.log(`  ${index + 1}. ${detalle.nombrePlatillo} - Status: ${detalle.status} - Impreso: ${detalle.impreso} - Es guardado: ${esGuardado}`);

            return {
              idPedidoDetalle: detalle.idPedidoDetalle || generarIdUnico(),
              idProducto: detalle.idPlatillo,
              idPlatillo: detalle.idPlatillo,
              nombre: detalle.nombrePlatillo,
              precio: parseFloat(detalle.precio) || 0,
              cantidad: parseInt(detalle.cantidad, 10) || 1,
              observaciones: detalle.observaciones || [],
              subtotal: parseFloat(detalle.precio) * (parseInt(detalle.cantidad, 10) || 1),
              status: esGuardado ? 'guardado' : 'nuevo',
              guardarEnBD: !esGuardado,
              impreso: esGuardado,
              impresora: detalle.impresora || systemConfig?.impresoraPorDefecto || 'local',
              imprimirCocina: detalle.imprimirCocina || 1
            };
          });

          // Separar productos para logging
          const guardados = itemsPedido.filter(item => item.status === 'guardado');
          const nuevos = itemsPedido.filter(item => item.status === 'nuevo');

          console.log(`✅ Productos separados:`);
          console.log(`  🆕 Nuevos: ${nuevos.length}`);
          console.log(`  💾 Guardados: ${guardados.length}`);

          setPedidoExistente({
            ...pedidoData,
            itemsDetalle: itemsPedido
          });

          inicializarPedido(itemsPedido);
          setModoEdicion(true);

          toast.success(`Pedido cargado: ${itemsPedido.length} productos (${guardados.length} guardados, ${nuevos.length} nuevos)`);
        }
      } else {
        // Si no hay pedido existente, abrir el modal de número de personas automáticamente
        setTimeout(() => {
          setModalNumeroPersonas(true);
        }, 500); // Pequeño delay para que la interfaz se cargue completamente
      }

      inicializacionCompleta.current = true;
      setLoading(false);
    } catch (err) {
      console.error("Error al cargar datos iniciales:", err);
      setError("No se pudieron cargar los datos necesarios. Por favor, intente de nuevo.");
      setLoading(false);
      toast.error("Error al cargar datos iniciales del pedido");
    }
  }, [idMesa, cargarCategoriaYProductos, inicializarPedido, systemConfig, toast]);

  // ===== CONFIGURACIÓN PARA TOMAPEDIDOS =====

  // Información del pedido para el header
  const infoPedido = useMemo(() => {
    return {
      numeroPedido: numeroCuenta,
      numeroMesa: datosMesa?.numero || idMesa,
      nombreMesa: datosMesa?.caption || `Mesa ${datosMesa?.numero || idMesa}`,
      mesero: mesero,
      numeroPersonas: numeroPersonas,
      status: modoEdicion ? 'abierto' : 'nuevo',
      subtotal: calcularTotales()?.subtotal || 0,
      descuentos: totalDescuentos || 0,
      total: calcularTotales()?.total || 0,
      fechaCreacion: fechaHora,
      // Callbacks para editar información
      onEditarMesa: () => {
        console.log('onEditarMesa callback ejecutado');
        setModalCambioMesaVisible(true);
      },
      onEditarMesero: () => {
        console.log('onEditarMesero callback ejecutado');
        setModalMeseroVisible(true);
      },
      onEditarPersonas: () => {
        console.log('onEditarPersonas callback ejecutado');
        setModalNumeroPersonas(true);
      }
    };
  }, [numeroCuenta, datosMesa, idMesa, mesero, numeroPersonas, modoEdicion,
    calcularTotales, totalDescuentos, fechaHora]);

  // Configuración de botones
  const botonesConfig = useMemo(() => {
    const hayProductos = pedidoActual.length > 0;
    const hayProductosNuevos = productosNuevos.length > 0;

    return {
      finalizar: {
        visible: true,
        habilitado: hayProductos && !loading,
        texto: 'Finalizar Pedido'
      },
      guardar: {
        visible: true,
        habilitado: hayProductosNuevos && !loading,
        texto: 'Guardar Cambios'
      },
      imprimir: {
        visible: true,
        habilitado: hayProductosNuevos && !loading,
        texto: 'Imprimir Comanda'
      },
      guardarEImprimir: {
        visible: true,
        habilitado: hayProductosNuevos && !loading,
        texto: 'Guardar + Imprimir'
      },
      limpiar: {
        visible: true,
        habilitado: hayProductos && !loading,
        texto: 'Limpiar Pedido'
      },
      pagarCuenta: { visible: false }, // No aplica en mesa abierta
      cancelar: { visible: false },
      regresar: {
        visible: true,
        habilitado: true,
        texto: 'Regresar a Mesas'
      },
      // Configuración específica para el botón de impresoras
      mostrarSemaforoImpresoras: true,
      verificadorImpresoras: verificadorImpresoras,


    };
  }, [pedidoActual.length, productosNuevos.length, loading]);

  // ===== MANEJADORES DE EVENTOS PARA TOMAPEDIDOS =====
  const verificarImpresorasAntes = useCallback(async (callback) => {
    try {
      if (!verificadorImpresoras || typeof verificadorImpresoras.verificarImpresoras !== 'function') {
        // Si no hay verificador, continuar sin verificación
        await callback();
        return true;
      }

      const resultado = await verificadorImpresoras.verificarImpresoras();

      if (resultado && resultado.todasDisponibles) {
        // Todas las impresoras están disponibles, continuar
        await callback();
        return true;
      } else {
        // Hay problemas con las impresoras, mostrar diálogo
        setResultadoVerificacionImpresoras(resultado);
        setMostrarDialogoImpresoras(true);
        return false;
      }
    } catch (error) {
      console.error("Error al verificar impresoras:", error);
      // En caso de error, preguntar al usuario
      const continuar = window.confirm("No se pudo verificar el estado de las impresoras. ¿Desea continuar?");
      if (continuar) {
        await callback();
        return true;
      }
      return false;
    }
  }, []);
  const handleFinalizar = useCallback(async () => {
    if (pedidoActual.length === 0) {
      toast.error("Debe agregar al menos un producto al pedido");
      return;
    }

    try {
      let pedidoIdParaPago = pedidoId || pedidoExistente?.idPedido;

      // Primero guardar el pedido si hay productos nuevos
      if (productosNuevos.length > 0) {
        const respuestaGuardar = await handleGuardar();
        // Si handleGuardar retorna un nuevo pedidoId, usarlo
        if (respuestaGuardar && respuestaGuardar.idPedido) {
          pedidoIdParaPago = respuestaGuardar.idPedido;
        }
      }

      // Validar que tenemos un ID de pedido
      if (!pedidoIdParaPago) {
        toast.error("No se pudo obtener el ID del pedido. Intente guardar primero.");
        return;
      }

      // Preparar datos para el pago
      const totales = calcularTotales();
      console.log("🔧 Totales calculados para pago:", totales);
      console.log("🔧 PedidoId final para pago:", pedidoIdParaPago);
      console.log("🔧 Pedido actual:", pedidoActual);

      // Calcular el total manualmente desde pedidoActual si totales.total es 0
      const totalCalculado = pedidoActual.reduce((sum, item) => {
        return sum + (parseFloat(item.precio || 0) * parseInt(item.cantidad || 0));
      }, 0);

      const totalFinal = totales.total || totalCalculado;

      const datosPago = {
        idPedido: pedidoIdParaPago,
        totalCuenta: totalFinal,
        pedidoInfo: {
          idPedido: pedidoIdParaPago,
          numeroMesa: datosMesa?.numero || idMesa,
          nombreMesa: datosMesa?.caption || `Mesa ${datosMesa?.numero || idMesa}`,
          mesero: mesero,
          numeroPersonas: numeroPersonas,
          detalles: pedidoActual,
          descuentos: totalDescuentos || 0
        },
        subtotal: totales.subtotal || totalCalculado,
        total: totalFinal
      };

      // Datos preparados para el pago, navegación directa sin localStorage
      console.log("🔧 Datos preparados para pago:", datosPago);

      // Navegar a la ruta con ID del pedido
      navigate(`/pagar-pedido/${pedidoIdParaPago}`);

    } catch (error) {
      console.error("Error al finalizar pedido:", error);
      toast.error("Error al preparar el pago. Intente nuevamente.");
    }
  }, [pedidoActual, productosNuevos, handleGuardar, calcularTotales, idMesa, datosMesa, numeroPersonas,
    mesero, totalDescuentos, pedidoId, pedidoExistente, navigate, toast]);

  const handleGuardar = useCallback(async () => {
    if (productosNuevos.length === 0) {
      toast.info("No hay cambios nuevos para guardar");
      return;
    }

    try {
      setLoading(true);
      const usuario = API.auth.getCurrentUser();

      console.log("🔧 DEBUG handleGuardar:");
      console.log("  - pedidoId actual:", pedidoId);
      console.log("  - productos nuevos:", productosNuevos.length);
      console.log("  - productos guardados:", productosGuardados.length);

      // Si NO existe pedido, crear uno nuevo
      if (!pedidoId) {
        console.log("🆕 Creando pedido nuevo (pedidoId es null/undefined)...");

        const totales = calcularTotales();
        const pedidoData = {
          idMesa: parseInt(idMesa),
          numeroMesa: datosMesa?.numero || parseInt(idMesa),
          nombreMesa: datosMesa?.caption || `Mesa ${datosMesa?.numero || idMesa}`,
          numeroPersonas: parseInt(numeroPersonas),
          idMesero: mesero?.idMesero,
          nombreMesero: mesero?.nombre || "Sin mesero",
          usuario: usuario?.usuario || usuario?.nombre || 'Admin',
          detalles: productosNuevos.map(producto => ({
            idPlatillo: producto.idProducto,
            nombrePlatillo: producto.nombre,
            cantidad: producto.cantidad,
            precio: producto.precio,
            costo: 0,
            observaciones: producto.observaciones || [],
            instrucciones: producto.instrucciones || '',
            impresora: producto.impresora || systemConfig?.impresoraPorDefecto || 'Local',
            imprimirCocina: producto.imprimirCocina || 1
          })),
          subtotal: totales.subtotal,
          descuentos: totales.descuentos || 0,
          total: totales.total,
          promociones: promocionesAplicadas || []
        };

        const respuesta = await API.pedidos.crearPedido(pedidoData);

        if (respuesta?.idPedido) {
          setPedidoId(respuesta.idPedido);
          toast.success("Pedido creado y productos guardados correctamente");
          return { idPedido: respuesta.idPedido }; // Retornar el ID para handleFinalizar
        }
      }
      // Si YA existe pedido, solo agregar productos individuales
      else {
        console.log("📦 Agregando productos a pedido existente:", pedidoId);

        // Agregar cada producto individualmente
        for (const producto of productosNuevos) {
          // Calcular precio final basado en promociones aplicadas (misma lógica que handleRegresar)
          let precioFinal = producto.precio;
          let tienePromocion = false;
          let idPromocion = 0;
          let descuentoAplicado = 0;

          // Buscar si este producto específico tiene promociones aplicadas
          for (const promo of promocionesAplicadas || []) {
            // Verificar si es promoción tipo 1 (descuento directo)
            if (promo.tipo === '1' || promo.tipo === 1) {
              if (promo.productos && promo.productos.includes(producto.idPedidoDetalle)) {
                const porcentajeDescuento = promo.porcentajeDescuento / 100;
                precioFinal = producto.precio * (1 - porcentajeDescuento);
                tienePromocion = true;
                idPromocion = promo.idPromocion || 0;
                break;
              }
            }
            // Verificar si es promoción tipo 2 (2x1, 3x2, etc.)
            else if (promo.tipo === '2' || promo.tipo === 2) {
              if (promo.productosGratis && promo.productosGratis.includes(producto.idPedidoDetalle)) {
                const porcentajeDescuento = promo.porcentajeDescuento / 100;
                precioFinal = producto.precio * (1 - porcentajeDescuento);
                tienePromocion = true;
                idPromocion = promo.idPromocion || 0;
                break;
              }
              else if (promo.productosIncluidos && promo.productosIncluidos.includes(producto.idPedidoDetalle)) {
                tienePromocion = true;
                idPromocion = promo.idPromocion || 0;
                break;
              }
            }
            // Verificar si es promoción tipo 3 (combos)
            else if (promo.tipo === '3' || promo.tipo === 3) {
              if (promo.productosGratis && promo.productosGratis.includes(producto.idPedidoDetalle)) {
                precioFinal = 0;
                tienePromocion = true;
                idPromocion = promo.idPromocion || 0;
                break;
              }
              else if (promo.productosIncluidos && promo.productosIncluidos.includes(producto.idPedidoDetalle)) {
                tienePromocion = true;
                idPromocion = promo.idPromocion || 0;
                break;
              }
            }
          }

          const productoData = {
            idPlatillo: producto.idProducto,
            nombrePlatillo: producto.nombre,
            cantidad: producto.cantidad,
            precio: producto.precio, // Precio original
            precioFinal: precioFinal, // Precio con promoción aplicada
            costo: 0,
            observaciones: producto.observaciones || [],
            instrucciones: producto.instrucciones || '',
            idCategoria: producto.idCategoria || 0,
            nombreCategoria: producto.nombreCategoria || '',
            promocionAplicada: tienePromocion,
            idPromocion: idPromocion
          };

          console.log("Agregando producto:", productoData);
          await API.pedidos.agregarProducto(pedidoId, productoData);
        }

        toast.success("Productos agregados correctamente al pedido existente");
      }

      // Marcar productos como guardados después del guardado exitoso
      if (productosNuevos.length > 0) {
        // Actualizar el estado de los productos a 'guardado'
        const productosActualizados = pedidoActual.map(producto => {
          if (productosNuevos.some(nuevo => nuevo.idPedidoDetalle === producto.idPedidoDetalle)) {
            return { ...producto, status: 'guardado', impreso: false };
          }
          return producto;
        });

        // Reinicializar el pedido con los productos actualizados
        inicializarPedido(productosActualizados);
        return { idPedido: pedidoId }; // Retornar el ID existente
      }

      setLoading(false);
      return { idPedido: pedidoId || pedidoExistente?.idPedido };
    } catch (error) {
      console.error("Error al guardar:", error);
      toast.error(`Error al guardar: ${error.message}`);
      setLoading(false);
      return null; // Retornar null en caso de error
    }
  }, [productosNuevos, calcularTotales, idMesa, datosMesa, numeroPersonas,
    mesero, systemConfig, promocionesAplicadas, pedidoId, toast]);

  // ===== LÓGICA DE IMPRESIÓN CORREGIDA =====

  // ===== LÓGICA DE IMPRESIÓN CORREGIDA =====

  const handleImprimir = useCallback(async () => {
    console.log("🖨️ Iniciando proceso de impresión...");

    // Validación inicial
    if (productosNuevos.length === 0) {
      toast.warning("No hay productos nuevos para imprimir");
      return;
    }

    try {
      setLoading(true);
      toast.info("Preparando impresión...");

      // PASO 1: Verificar que el servicio de ComandaService esté disponible
      let servicioDisponible = false;
      try {
        // Intentar verificar si el servicio está inicializado
        if (ComandaService && typeof ComandaService.initialize === 'function') {
          await ComandaService.initialize();
          servicioDisponible = true;
        } else if (ComandaService) {
          servicioDisponible = true;
        }
      } catch (serviceError) {
        console.error("Error al verificar servicio:", serviceError);
        servicioDisponible = false;
      }

      if (!servicioDisponible) {
        toast.error("El servicio de impresión no está disponible. Verifica que la aplicación esté ejecutándose correctamente.");
        setLoading(false);
        return;
      }

      // PASO 2: Filtrar y validar productos para impresión
      const productosParaImprimir = productosNuevos.filter(producto => {
        // Verificar si el producto debe imprimirse
        const debeImprimir = producto.imprimirCocina === 1 ||
          producto.imprimirCocina === '1' ||
          producto.imprimirCocina === true;

        // Log para debug
        console.log(`Producto ${producto.nombre}: imprimirCocina=${producto.imprimirCocina}, debeImprimir=${debeImprimir}`);

        return debeImprimir;
      });

      console.log(`Total productos nuevos: ${productosNuevos.length}`);
      console.log(`Productos para imprimir: ${productosParaImprimir.length}`);

      if (productosParaImprimir.length === 0) {
        toast.warning("Ningún producto está configurado para impresión en cocina");
        setLoading(false);
        return;
      }

      // PASO 3: Preparar datos del pedido
      const infoPedidoImpresion = {
        mesa: {
          numero: datosMesa?.numero || idMesa,
          nombre: datosMesa?.caption || datosMesa?.nombre || `Mesa ${datosMesa?.numero || idMesa}`,
          id: idMesa
        },
        mesero: {
          nombre: mesero?.nombre || mesero?.name || "Sin mesero asignado",
          id: mesero?.id || mesero?.idMesero || null
        },
        numeroPersonas: numeroPersonas || 1,
        numeroCuenta: numeroCuenta || '00',
        fechaHora: new Date()
      };

      console.log("Info del pedido para impresión:", infoPedidoImpresion);

      // PASO 4: Validar que los productos tengan la estructura correcta
      const productosValidados = productosParaImprimir.map(producto => ({
        idProducto: producto.idProducto || producto.idPlatillo,
        nombre: producto.nombre || 'Producto sin nombre',
        cantidad: parseInt(producto.cantidad) || 1,
        precio: parseFloat(producto.precio) || 0,
        observaciones: Array.isArray(producto.observaciones) ? producto.observaciones : [],
        impresora: producto.impresora || systemConfig?.impresoraPorDefecto || 'Local',
        imprimirCocina: producto.imprimirCocina
      }));

      // Impresión en segundo plano (fire-and-forget, no bloquea la UI)
      const imprimirEnSegundoPlano = async () => {
        try {
          let resultadoImpresion = null;

          if (typeof ComandaService.imprimirProductosPendientes === 'function') {
            resultadoImpresion = await ComandaService.imprimirProductosPendientes(
              productosValidados, infoPedidoImpresion, systemConfig
            );
          } else if (typeof ComandaService.imprimirComanda === 'function') {
            const fechaFormateada = ComandaService.formatearFecha ?
              ComandaService.formatearFecha(new Date()) : new Date().toLocaleDateString();
            const horaFormateada = ComandaService.formatearHora ?
              ComandaService.formatearHora(new Date()) : new Date().toLocaleTimeString();

            resultadoImpresion = await ComandaService.imprimirComanda(
              productosValidados, infoPedidoImpresion.mesa, infoPedidoImpresion.mesero,
              fechaFormateada, horaFormateada,
              systemConfig?.impresoraPorDefecto || 'Local',
              { tipoPedido: 'mesa', nombreCliente: '', numeroPedido: infoPedidoImpresion.numeroCuenta || '' }
            );
          } else if (typeof imprimirComanda === 'function') {
            resultadoImpresion = await imprimirComanda(productosValidados, infoPedidoImpresion);
          }

          if (resultadoImpresion && !resultadoImpresion.success) {
            console.error('Error en impresión:', resultadoImpresion.message);
          }
        } catch (err) {
          console.error('Error en impresión en segundo plano:', err);
        }
      };

      imprimirEnSegundoPlano(); // No await - fire and forget
      toast.success('Comanda enviada a impresión');

    } catch (error) {
      console.error("Error en handleImprimir:", error);
      toast.error(error.message || "Error durante la impresión");
    } finally {
      setLoading(false);
    }
  }, [
    productosNuevos,
    datosMesa,
    idMesa,
    mesero,
    numeroPersonas,
    numeroCuenta,
    systemConfig,
    toast,
    imprimirComanda
  ]);

  // ===== FUNCIÓN AUXILIAR PARA DEBUG =====
  const debugImpresion = useCallback(() => {
    console.log("=== DEBUG IMPRESIÓN ===");
    console.log("Productos nuevos:", productosNuevos);
    console.log("Sistema config:", systemConfig);
    console.log("ComandaService disponible:", !!ComandaService);
    console.log("Métodos disponibles en ComandaService:", ComandaService ? Object.keys(ComandaService) : 'N/A');
    console.log("Hook imprimirComanda disponible:", typeof imprimirComanda);
    console.log("========================");
  }, [productosNuevos, systemConfig, imprimirComanda]);

  // Llamar debug en desarrollo (opcional)
  // useEffect(() => {
  //   if (process.env.NODE_ENV === 'development') {
  //     debugImpresion();
  //   }
  // }, [debugImpresion]);

  // Método auxiliar para verificar impresoras (opcional)
  const verificarEstadoImpresoras = useCallback(async () => {
    try {
      const impresoras = await ComandaService.getImpresorasDisponibles(true);

      if (!impresoras || impresoras.length === 0) {
        toast.warning("No se encontraron impresoras en el sistema");
        return false;
      }

      const impresorasOffline = impresoras.filter(imp => !imp.disponible);

      if (impresorasOffline.length > 0) {
        const nombres = impresorasOffline.map(imp => imp.name).join(', ');
        toast.warning(`Impresoras offline: ${nombres}`);
      }

      const impresorasOnline = impresoras.filter(imp => imp.disponible);
      toast.success(`${impresorasOnline.length} impresoras disponibles`);

      return impresorasOnline.length > 0;

    } catch (error) {
      console.error("Error al verificar impresoras:", error);
      toast.error(`Error al verificar impresoras: ${error.message}`);
      return false;
    }
  }, [toast]);

  // Método mejorado para guardar e imprimir
  const handleGuardarEImprimir = useCallback(async () => {
    if (productosNuevos.length === 0) {
      toast.warning("No hay productos para guardar e imprimir");
      return;
    }

    try {
      setLoading(true);

      // Primero guardar
      toast.info("Guardando pedido...");
      await handleGuardar();

      // Luego imprimir
      toast.info("Imprimiendo comandas...");
      await handleImprimir();

      toast.success("Pedido guardado e impreso correctamente");

    } catch (error) {
      console.error("Error en guardar e imprimir:", error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [handleGuardar, handleImprimir, productosNuevos, toast]);



  const handleLimpiar = useCallback(() => {
    if (pedidoActual.length === 0) {
      toast.info("No hay productos en el pedido para limpiar");
      return;
    }

    if (window.confirm("¿Está seguro de limpiar todo el pedido? Esta acción no se puede deshacer.")) {
      limpiarPedido();
      toast.warning("Pedido limpiado completamente");
    }
  }, [pedidoActual, limpiarPedido, toast]);

  const handleRegresar = useCallback(async () => {
    if (productosNuevos.length > 0 && pedidoId) {
      try {
        toast.info("Guardando productos nuevos...");

        // Calcular totales actuales con promociones
        const totalesActuales = calcularTotales();
        console.log("🔧 DEBUG totales actuales:", totalesActuales);
        console.log("🔧 DEBUG promociones aplicadas:", promocionesAplicadas);

        // Preparar productos para guardar solo en pedido_detalle
        const productosParaGuardar = productosNuevos.map(producto => {
          // Calcular precio final basado en promociones aplicadas
          let precioFinal = producto.precio; // Por defecto, precio original
          let tienePromocion = false;
          let idPromocion = 0;
          let descuentoAplicado = 0;

          // Buscar si este producto específico tiene promociones aplicadas
          for (const promo of promocionesAplicadas || []) {
            console.log(`🔧 Revisando promoción ${promo.idPromocion} para producto ${producto.nombre}:`);
            console.log(`🔧 Promoción:`, promo);

            // Verificar si es promoción tipo 1 (descuento directo)
            if (promo.tipo === '1' || promo.tipo === 1) {
              if (promo.productos && promo.productos.includes(producto.idPedidoDetalle)) {
                const porcentajeDescuento = promo.porcentajeDescuento / 100;
                precioFinal = producto.precio * (1 - porcentajeDescuento);
                descuentoAplicado = (producto.precio - precioFinal) * producto.cantidad;
                tienePromocion = true;
                idPromocion = promo.idPromocion || 0;
                console.log(`🔧 Promoción T1 aplicada - Producto ${producto.nombre}: precio original ${producto.precio}, precio final ${precioFinal}, descuento ${descuentoAplicado}`);
                break;
              }
            }

            // Verificar si es promoción tipo 2 (2x1, 3x2, etc.)
            else if (promo.tipo === '2' || promo.tipo === 2) {
              // Producto está en productos gratis (con descuento)
              if (promo.productosGratis && promo.productosGratis.includes(producto.idPedidoDetalle)) {
                const porcentajeDescuento = promo.porcentajeDescuento / 100;
                precioFinal = producto.precio * (1 - porcentajeDescuento);
                descuentoAplicado = (producto.precio - precioFinal) * producto.cantidad;
                tienePromocion = true;
                idPromocion = promo.idPromocion || 0;
                console.log(`🔧 Promoción T2 (gratis) aplicada - Producto ${producto.nombre}: precio original ${producto.precio}, precio final ${precioFinal}, descuento ${descuentoAplicado}`);
                break;
              }
              // Producto está en productos incluidos (precio normal)
              else if (promo.productosIncluidos && promo.productosIncluidos.includes(producto.idPedidoDetalle)) {
                // Para productos que se cobran normal, el precio no cambia, pero sí está en promoción
                tienePromocion = true;
                idPromocion = promo.idPromocion || 0;
                console.log(`🔧 Promoción T2 (normal) aplicada - Producto ${producto.nombre}: precio se mantiene ${producto.precio}`);
                break;
              }
            }

            // Verificar si es promoción tipo 3 (combos)
            else if (promo.tipo === '3' || promo.tipo === 3) {
              // Similar lógica para tipo 3 si es necesario
              if (promo.productosGratis && promo.productosGratis.includes(producto.idPedidoDetalle)) {
                precioFinal = 0; // Productos gratis en combos
                descuentoAplicado = producto.precio * producto.cantidad;
                tienePromocion = true;
                idPromocion = promo.idPromocion || 0;
                console.log(`🔧 Promoción T3 (gratis) aplicada - Producto ${producto.nombre}: precio original ${producto.precio}, precio final ${precioFinal}, descuento ${descuentoAplicado}`);
                break;
              }
              else if (promo.productosIncluidos && promo.productosIncluidos.includes(producto.idPedidoDetalle)) {
                // Precio normal para productos principales del combo
                tienePromocion = true;
                idPromocion = promo.idPromocion || 0;
                console.log(`🔧 Promoción T3 (normal) aplicada - Producto ${producto.nombre}: precio se mantiene ${producto.precio}`);
                break;
              }
            }
          }

          const productoFormateado = {
            idPlatillo: producto.idProducto,
            nombrePlatillo: producto.nombre,
            cantidad: producto.cantidad,
            precio: producto.precio, // Precio original del producto
            precioFinal: precioFinal, // Precio final con promoción aplicada
            costo: producto.costo || 0,
            observaciones: producto.observaciones || [],
            instrucciones: producto.instrucciones || '',
            idCategoria: producto.idCategoria || 0,
            nombreCategoria: producto.nombreCategoria || '',
            promocionAplicada: tienePromocion,
            idPromocion: idPromocion,
            descuentoAplicado: descuentoAplicado
          };

          console.log("🔧 DEBUG producto preparado para guardar:", productoFormateado);
          return productoFormateado;
        });

        // Usar el nuevo endpoint que solo guarda productos sin modificar el pedido
        await API.pedidos.guardarProductosNuevos(pedidoId, productosParaGuardar);

        toast.success("Productos guardados correctamente en la base de datos");
        navigate('/ventas');
      } catch (error) {
        console.error("Error al guardar productos:", error);
        toast.error("Error al guardar productos. ¿Desea salir sin guardar?");
        if (window.confirm("Hay productos sin guardar. ¿Desea salir sin guardar?")) {
          navigate('/ventas');
        }
      }
    } else if (productosNuevos.length > 0 && !pedidoId) {
      // Si no hay pedidoId, crear un pedido completo como antes
      try {
        toast.info("Guardando productos nuevos...");
        await handleGuardar();
        toast.success("Productos guardados correctamente");
        navigate('/ventas');
      } catch (error) {
        console.error("Error al guardar:", error);
        toast.error("Error al guardar productos. ¿Desea salir sin guardar?");
        if (window.confirm("Hay productos sin guardar. ¿Desea salir sin guardar?")) {
          navigate('/ventas');
        }
      }
    } else {
      navigate('/ventas');
    }
  }, [productosNuevos, pedidoId, handleGuardar, navigate, toast]);

  // ===== MANEJADORES PARA CREACIONPEDIDO (TU CÓDIGO ORIGINAL) =====

  const cambiarCategoria = useCallback(async (categoria) => {
    try {
      setCategoriaSeleccionada(categoria);
      setLoadingProductos(true);

      const productosData = await API.productos.getPorCategoria(categoria.idCategoria);
      setProductos(productosData);
      setLoadingProductos(false);
      toast.success(`${productosData.length} productos cargados`);
    } catch (err) {
      console.error(`Error al cargar productos de la categoría ${categoria.nombre}:`, err);
      setError(`No se pudieron cargar los productos de ${categoria.nombre}`);
      setProductos([]);
      setLoadingProductos(false);
      toast.error(`Error al cargar productos de ${categoria.nombre}`);
    }
  }, [toast]);

  const handleEditarInstrucciones = useCallback((idPedidoDetalle, instrucciones) => {
    // Actualizar el producto en el pedido actual con las nuevas instrucciones
    const pedidoActualizado = pedidoActual.map(item => {
      if (item.idPedidoDetalle === idPedidoDetalle) {
        return { ...item, instrucciones };
      }
      return item;
    });

    // Actualizar el estado del pedido
    inicializarPedido(pedidoActualizado);

    toast.success("Instrucciones actualizadas");
  }, [pedidoActual, inicializarPedido, toast]);

  const handleAgregarProducto = useCallback((producto) => {
    if (!producto || !producto.nombre) {
      console.warn("Intento de agregar producto inválido:", producto);
      toast.error("Error: Producto inválido");
      return;
    }

    const productoFormateado = {
      idPedidoDetalle: generarIdUnico(),
      idProducto: producto.idProducto || producto.idPlatillo,
      nombre: producto.nombre,
      precio: parseFloat(producto.precio) || 0,
      cantidad: 1,
      observaciones: [],
      subtotal: parseFloat(producto.precio) || 0,
      status: 'nuevo',
      guardarEnBD: true,
      impresora: producto.impresora || systemConfig?.impresoraPorDefecto || 'Local',
      imprimirCocina: producto.imprimirCocina !== undefined ?
        producto.imprimirCocina : 1,
      // USAR información de categoría que viene directamente del producto desde la API
      idCategoria: producto.idCategoria || 0,
      nombreCategoria: producto.nombreCategoria || ''
    };

    console.log("🔧 DEBUG handleAgregarProducto - Producto original:", producto);
    console.log("🔧 DEBUG handleAgregarProducto - Categoría seleccionada:", categoriaSeleccionada);
    console.log("🔧 DEBUG handleAgregarProducto - Producto formateado:", productoFormateado);

    const existeIndex = pedidoActual.findIndex(item =>
      (item.idProducto === productoFormateado.idProducto && item.status === 'nuevo') ||
      (item.nombre === productoFormateado.nombre && item.precio === productoFormateado.precio && item.status === 'nuevo')
    );

    if (existeIndex >= 0) {
      const nuevaCantidad = pedidoActual[existeIndex].cantidad + 1;
      cambiarCantidad(pedidoActual[existeIndex].idPedidoDetalle, nuevaCantidad);
    } else {
      agregarProducto(productoFormateado);
      toast.success(`${producto.nombre} agregado al pedido`);
    }

    // Recalcular promociones si es necesario
    if (recalcularPromociones) {
      recalcularPromociones();
    }
  }, [agregarProducto, cambiarCantidad, pedidoActual, recalcularPromociones, systemConfig, toast]);

  // ===== FUNCIONES DE VERIFICACIÓN DE IMPRESORAS =====



  const handleContinuarSinImpresoras = useCallback(async () => {
    setMostrarDialogoImpresoras(false);
    // Continuar con la operación pendiente
    toast.warning("Continuando sin verificar impresoras1");
  }, [toast]);

  const handleRevisarImpresoras = useCallback(async () => {
    setMostrarDialogoImpresoras(false);
    setMostrarMonitorImpresoras(true);
  }, []);

  const handleCancelarImpresion = useCallback(() => {
    setMostrarDialogoImpresoras(false);
    toast.info("Operación cancelada. Revise las impresoras antes de continuar.");
  }, [toast]);

  // ===== MANEJADORES DE MODALES (TU CÓDIGO ORIGINAL) =====

  // ***** MODAL DE MESERO *****
  const abrirModalMesero = useCallback(() => {
    setModalMeseroVisible(true);
  }, []);

  const seleccionarMesero = useCallback(async (meseroSeleccionado) => {
    try {
      const meseroAnterior = mesero;
      setMesero(meseroSeleccionado);
      setModalMeseroVisible(false);

      // Si hay un pedido existente, actualizar el mesero en la base de datos
      if (pedidoExistente && pedidoExistente.idPedido) {
        const pedidoData = {
          idPedido: pedidoExistente.idPedido,
          idMesa: parseInt(idMesa),
          numeroMesa: datosMesa?.numero,
          nombreMesa: datosMesa?.caption,
          numeroPersonas: numeroPersonas,
          idMesero: meseroSeleccionado?.idMesero,
          nombreMesero: meseroSeleccionado?.nombre || "Sin mesero"
        };

        const respuesta = await API.pedidos.actualizarPedido(pedidoData);

        if (respuesta.success) {
          toast.success(`Mesero actualizado a: ${meseroSeleccionado.nombre}`);
        } else {
          // Revertir el cambio si falló
          setMesero(meseroAnterior);
          throw new Error(respuesta.message || "Error al actualizar mesero");
        }
      } else {
        toast.success(`Mesero asignado: ${meseroSeleccionado.nombre}`);
      }
    } catch (err) {
      console.error("Error al actualizar mesero:", err);
      toast.error("Error al actualizar mesero: " + err.message);
    }
  }, [pedidoExistente, idMesa, datosMesa, numeroPersonas, mesero, toast]);

  const cerrarModalMesero = useCallback(() => {
    setModalMeseroVisible(false);
  }, []);

  // ***** MODAL DE CAMBIO DE MESA *****
  const abrirModalCambioMesa = useCallback(() => {
    setModalCambioMesaVisible(true);
  }, []);

  const confirmarCambioMesa = useCallback(async (nuevaMesa) => {
    if (!nuevaMesa || !pedidoExistente) {
      toast.error("No se puede cambiar de mesa sin un pedido activo");
      return;
    }

    try {
      setLoading(true);

      const datos = {
        idPedido: pedidoExistente.idPedido,
        idMesaOriginal: parseInt(idMesa),
        idMesaDestino: nuevaMesa.idMesa,
        numeroMesaDestino: nuevaMesa.numero,
        nombreMesaDestino: nuevaMesa.caption || nuevaMesa.nombre
      };

      const respuesta = await API.pedidos.cambiarMesa(datos);

      if (respuesta.success) {
        toast.success(`Pedido movido a ${nuevaMesa.caption || 'Mesa ' + nuevaMesa.numero}`);
        setModalCambioMesaVisible(false);

        // Redirigir a la nueva mesa
        navigate(`/mesa-detalle/${nuevaMesa.idMesa}`);
      } else {
        throw new Error(respuesta.message || "Error al cambiar de mesa");
      }
    } catch (error) {
      console.error("Error al cambiar mesa:", error);
      toast.error("Error al cambiar de mesa: " + error.message);
    } finally {
      setLoading(false);
    }
  }, [pedidoExistente, idMesa, navigate, toast]);

  const cerrarModalCambioMesa = useCallback(() => {
    setModalCambioMesaVisible(false);
  }, []);

  // ***** MODAL DE NÚMERO DE PERSONAS *****
  const abrirModalNumeroPersonas = useCallback(() => {
    setNumeroPersonasTemp(numeroPersonas);
    setModalNumeroPersonas(true);
  }, [numeroPersonas]);

  const confirmarNumeroPersonas = useCallback(async () => {
    const nuevas = parseInt(numeroPersonasTemp);

    if (!nuevas || isNaN(nuevas) || nuevas <= 0) {
      toast.warning("Por favor, ingrese un número válido de personas");
      return;
    }

    if (nuevas === numeroPersonas) {
      setModalNumeroPersonas(false);
      return;
    }

    try {
      const personasAnterior = numeroPersonas;
      setNumeroPersonas(nuevas);
      setModalNumeroPersonas(false);

      // Si hay un pedido existente, actualizar en la base de datos
      if (pedidoExistente && pedidoExistente.idPedido) {
        const pedidoData = {
          idPedido: pedidoExistente.idPedido,
          idMesa: parseInt(idMesa),
          numeroMesa: datosMesa?.numero,
          nombreMesa: datosMesa?.caption,
          numeroPersonas: nuevas,
          idMesero: mesero?.idMesero,
          nombreMesero: mesero?.nombre || "Sin mesero"
        };

        const respuesta = await API.pedidos.actualizarPedido(pedidoData);

        if (respuesta.success) {
          toast.success(`Número de personas actualizado a: ${nuevas}`);
        } else {
          // Revertir el cambio si falló
          setNumeroPersonas(personasAnterior);
          throw new Error(respuesta.message || "Error al actualizar número de personas");
        }
      } else {
        toast.success(`Número de personas: ${nuevas}`);
      }

      // Después de confirmar el número de personas, si no hay mesero asignado, abrir el modal de meseros
      if (!mesero) {
        setTimeout(() => {
          setModalMeseroVisible(true);
        }, 300); // Pequeño delay para permitir que se cierre el modal anterior
      }
    } catch (error) {
      console.error("Error al actualizar número de personas:", error);
      toast.error("Error al actualizar: " + error.message);
    }
  }, [numeroPersonasTemp, numeroPersonas, pedidoExistente, idMesa, datosMesa, mesero, toast]);

  const cancelarNumeroPersonas = useCallback(() => {
    setModalNumeroPersonas(false);
    setNumeroPersonasTemp(numeroPersonas);
  }, [numeroPersonas]);

  const handleKeyPressPersonas = useCallback((e) => {
    if (e.key === 'Enter') {
      confirmarNumeroPersonas();
    } else if (e.key === 'Escape') {
      cancelarNumeroPersonas();
    }
  }, [confirmarNumeroPersonas, cancelarNumeroPersonas]);

  const handleInputPersonas = useCallback((e) => {
    const value = e.target.value;
    // Solo permitir números
    if (value === '' || (/^\d+$/.test(value) && parseInt(value) <= 99)) {
      setNumeroPersonasTemp(value);
    }
  }, []);

  // ===== RENDER =====

  // Si está cargando, mostrar indicador
  if (loading && !datosMesa) {
    return (
      <div className="toma-pedidos-cargando">
        <div className="spinner"></div>
        <p>Cargando datos del pedido, por favor espere...</p>
      </div>
    );
  }

  // Si hay error, mostrar mensaje
  if (error) {
    return (
      <div className="error-mensaje">
        <p>{error}</p>
        <button onClick={() => navigate('/ventas')}>Volver</button>
      </div>
    );
  }

  return (
    <>
      {/* Componente TomaPedidos como header */}
      <TomaPedidos
        tipoPedido="mesa"
        infoPedido={infoPedido}
        botonesConfig={botonesConfig}
        onFinalizar={handleFinalizar}
        onGuardar={handleGuardar}
        onImprimir={handleImprimir}
        onGuardarEImprimir={handleGuardarEImprimir}
        onLimpiar={handleLimpiar}
        onRegresar={handleRegresar}
        loading={loading}
      >
        {/* Componente de creación de pedido */}
        <CreacionPedido
          categorias={categorias}
          productos={productos}
          pedidoActual={pedidoActual}
          productosNuevos={productosNuevos}
          productosGuardados={productosGuardados}
          promociones={promocionesDisponibles}
          promocionesAplicadas={promocionesAplicadas}
          calculandoPromociones={calculandoPromociones}
          onAgregarProducto={handleAgregarProducto}
          onCambiarCantidad={cambiarCantidad}
          onEliminarProducto={eliminarProducto}
          onEliminarProductoGuardado={eliminarProductoGuardado}
          onLimpiarPedido={handleLimpiar}
          onImprimirComanda={handleImprimir}
          onCambiarCategoria={cambiarCategoria}
          onEditarInstrucciones={handleEditarInstrucciones}
          categoriaSeleccionada={categoriaSeleccionada}
          loading={loadingProductos}
        />
      </TomaPedidos>

      {/* Modal de selección de mesero */}
      {modalMeseroVisible && (
        <div className="modal-overlay">
          <div className="modal-container">
            <MeseroGridSelector
              onMeseroSelect={seleccionarMesero}
              onCancel={cerrarModalMesero}
              meseroActual={mesero}
            />
          </div>
        </div>
      )}

      {/* Modal de cambio de mesa */}
      {modalCambioMesaVisible && (
        <div className="modal-overlay">
          <div className="modal-container">
            <CambioMesaModal
              onClose={cerrarModalCambioMesa}
              visible={modalCambioMesaVisible}
              mesaActual={datosMesa}
              pedidoActual={pedidoExistente}
              onConfirmar={confirmarCambioMesa}
              onCancelar={cerrarModalCambioMesa}
            />
          </div>
        </div>
      )}

      {/* Modal de número de personas */}
      {modalNumeroPersonas && (
        <div className="modal-overlay">
          <div className="modal-container modal-numero-personas">
            <div className="modal-header">
              <h2>Número de Personas</h2>
              <button
                className="close-button"
                onClick={cancelarNumeroPersonas}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="numeroPersonasInput">
                  ¿Cuántas personas van a comer?
                </label>
                <input
                  type="text"
                  id="numeroPersonasInput"
                  value={numeroPersonasTemp}
                  onChange={handleInputPersonas}
                  onKeyPress={handleKeyPressPersonas}
                  className="numero-personas-input"
                  placeholder="Ingrese el número de personas"
                  autoFocus
                  maxLength="2"
                />
                <div className="input-hint">
                  Máximo 99 personas
                </div>
              </div>

              <div className="personas-quick-buttons">
                <span className="quick-label">Acceso rápido:</span>
                {[1, 2, 3, 4, 5, 6, 8, 10].map(num => (
                  <button
                    key={num}
                    className={`quick-person-btn ${numeroPersonasTemp == num ? 'active' : ''}`}
                    onClick={() => setNumeroPersonasTemp(num.toString())}
                  >
                    {num}
                  </button>
                ))}
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="accept-button"
                onClick={confirmarNumeroPersonas}
                disabled={!numeroPersonasTemp || numeroPersonasTemp <= 0}
              >
                ✓ Confirmar
              </button>
              <button
                className="cancel-button"
                onClick={cancelarNumeroPersonas}
              >
                ✗ Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de alerta para impresoras */}
      <DialogoAlertaImpresoras
        isOpen={mostrarDialogoImpresoras}
        onClose={() => setMostrarDialogoImpresoras(false)}
        resultadoVerificacion={resultadoVerificacionImpresoras}
        onContinuar={handleContinuarSinImpresoras}
        onRevisar={handleRevisarImpresoras}
        onCancelar={handleCancelarImpresion}
      />

      {/* Modal para monitor de impresoras */}
      {mostrarMonitorImpresoras && (
        <div className="modal-overlay">
          <div className="modal-container monitor-modal">
            <ImpresionMonitor
              onClose={() => setMostrarMonitorImpresoras(false)}
              onStatusChange={(estado) => {
                console.log("Estado de impresoras actualizado:", estado);
              }}
              verificadorImpresoras={verificadorImpresoras}
              obtenerImpresorasNecesarias={API.impresoras?.getImpresorasNecesarias}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default PedidosMesa;